import { SampleType } from "types/shared";
import { parse } from "node-html-parser";
import removeMd from "remove-markdown";

export const truncate = (str: string, max: number, suffix: string) =>
  str.length < max ? str : `${str.substr(0, str.substr(0, max - suffix.length).lastIndexOf(" "))}${suffix}`;

export const sampleToSampleCard = (sample: SampleType) => {
  let excerpt = truncate(removeMd(sample.text), 220, "...").replace(/\*/gi, "");
  // markdown to html
  return {
    id: sample.id,
    heading: sample.title,
    category: {
      title: sample.subcategory.name,
      slug: `${sample.subcategory.category.slug}/${sample.subcategory.slug}`,
    },
    slug: sample.slug,
    date: sample.dateUpdated,
    words: sample.words,
    pages: sample.pages,
    views: sample.views,
    excerpt,
  };
};

export const lockScroll = () => {
  document.body.classList.add("menu-open");
  document.documentElement.classList.add("menu-open");
};

export const freeScroll = () => {
  document.body.classList.remove("menu-open");
  document.documentElement.classList.remove("menu-open");
};

export const lockMobileScroll = () => {
  document.body.classList.add("menu-open-xs");
  document.documentElement.classList.add("menu-open-xs");
};

export const freeMobileScroll = () => {
  document.body.classList.remove("menu-open-xs");
  document.documentElement.classList.remove("menu-open-xs");
};

const adjustColumnsWidth = (table: HTMLTableElement) => {
  const rows = Array.from(table.querySelectorAll("tr"));

  const cellsPerRowCounts: number[] = [];

  rows.forEach((row) => {
    const cells = Array.from(row.querySelectorAll("td, th"));
    const totalCellsCount = cells.reduce(
      (prev, next) => prev + (Number(next.getAttribute("colspan")) || 1),
      0
    );
    cellsPerRowCounts.push(totalCellsCount);
  });

  const totalRowsCount = Math.max(...cellsPerRowCounts);

  rows.forEach((row) => {
    const cells = Array.from(row.querySelectorAll("td, th"));

    cells.forEach((cell) => {
      cell.setAttribute("width", `${100 / totalRowsCount}%`);
    });
  });
};

const hasFixedWidthCells = (table: HTMLTableElement) => {
  const styledCells = Array.from(table.querySelectorAll("td[style], th[style]"));

  return styledCells.find((cell) => /width:/.test(String(cell.getAttribute("style"))));
};

const getFixedDimensions = (table: HTMLTableElement) => {
  const targetParent = table.closest("figure.table");
  if (!targetParent) return ["", ""];

  let width = "";
  let height = "";

  if (/width:/.test(String(targetParent.getAttribute("style")))) {
    width = window.getComputedStyle(targetParent).getPropertyValue("width");
  }

  if (/height:/.test(String(targetParent.getAttribute("style")))) {
    height = window.getComputedStyle(targetParent).getPropertyValue("height");
  }

  targetParent.removeAttribute("style");

  return [width, height];
};

const checkTableBorder = (table: HTMLTableElement) => {
  const tableStyle = table.getAttribute("style");
  const hasBorder = /border:/.test(String(tableStyle));
  if (hasBorder) {
    const value = window.getComputedStyle(table).getPropertyValue("border");
    table.setAttribute("style", tableStyle + ";border: none");
    return value;
  }
};

export const adjustArticleContent = (container: HTMLElement) => {
  const paragraphs = Array.from(container.querySelectorAll("p"));

  paragraphs.forEach((p) => {
    if (p.innerHTML === "&nbsp;") p.classList.add("empty");
  });

  const tables = Array.from(container.querySelectorAll("table"));

  tables.forEach((table) => {
    if (!hasFixedWidthCells(table)) {
      adjustColumnsWidth(table);
    }

    let wrapperStyle = "";

    const borderStyle = checkTableBorder(table);

    if (borderStyle) {
      wrapperStyle = "border:" + borderStyle + ";";
    }

    const fixedDimensions = getFixedDimensions(table);

    if (fixedDimensions[0]) {
      wrapperStyle += `width: ${fixedDimensions[0]};max-width: ${fixedDimensions[0]};`;
    }

    if (fixedDimensions[1]) {
      table.setAttribute("style", table.getAttribute("style") + `; height: ${fixedDimensions[1]}`);
    }

    table.outerHTML = `<div class="table-wrap"><div class="table-inner" style="${wrapperStyle}" >${table.outerHTML}</div></div>`;
  });
};

export const getH1 = (text?: string): undefined | string => {
  if (!text?.trim()) return;
  const dom = parse(text);
  const body = dom.getElementsByTagName("body")[0] || dom;

  const h1 = body.querySelector("h1");

  return h1?.textContent;
};

export const readRate = (text: string, wpm = 225): number => {
  const words = text.split(/\s+/).filter((w) => w != "").length;
  return Math.ceil(words / wpm);
};

export const getFormattedDate = (date: string) => {
  return `${new Date(date).toLocaleDateString("en-US", {
    day: "numeric",
  })} ${new Date(date).toLocaleDateString("en-US", {
    month: "short",
  })} ${new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
  })}`;
};

export const getContentsTableProps = (html: string, allHTags = false) => {
  const selectors = allHTags
    ? "h2:not(.no-menu), h3:not(.no-menu), h4:not(.no-menu), h5:not(.no-menu), h6:not(.no-menu)"
    : "h2:not(.no-menu)";

  const root = parse(html);
  const headings = root.querySelectorAll(selectors);
  headings.forEach((item, index) => {
    item.setAttribute("id", `heading-${index + 1}`);
  });

  return {
    menuItems: [...headings].map((el, index) => ({
      id: `heading-${index + 1}`,
      //remove first digits if any
      heading: String(el.textContent).replace(/^\d{1,}\.\s/, ""),
    })),
    processedContent: root.toString(),
  };
};
